import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { IoLocationOutline } from "react-icons/io5";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";

// TopoJSON source — world countries (110m resolution, no install needed)
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ---------------------------------------------------------------------------
// Glassmorphism price card — semi-transparent so countries show through
// ---------------------------------------------------------------------------
const RegionCard = styled(Box)(() => ({
  background: "rgba(8, 12, 35, 0.45)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  padding: "10px 12px",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(15, 20, 55, 0.65)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.18)",
    border: "1px solid rgba(37, 99, 235, 0.35)",
  },
}));

// ---------------------------------------------------------------------------
// Region configuration
// Cards are anchored BELOW their dot (transform: translate(-50%, 0)) so they
// never drift into the card header area at the top of the map container.
// Positions are calibrated for geoAzimuthalEqualArea centered at [15, 52].
// ---------------------------------------------------------------------------
const REGION_CONFIG = {
  drg_baseline: {
    color: "#A78BFA",
    coords: [10.5, 51.0],   // Central Germany
    cardPos: { top: "38%", left: "58%" },
  },
  centrally_tendered: {
    color: "#60A5FA",
    coords: [-2.0, 54.0],   // England
    cardPos: { top: "28%", left: "24%" },
  },
  strictly_capped: {
    color: "#10B981",
    coords: [3.0, 44.0],    // Southern France
    cardPos: { top: "62%", left: "38%" },
  },
};

// ---------------------------------------------------------------------------
// PulsingDot — pure SVG, rendered inside <Marker>
// ---------------------------------------------------------------------------
const PulsingDot = ({ color }) => (
  <g>
    {/* Outer animated ring */}
    <circle r={14} fill={color} opacity={0}>
      <animate attributeName="r" from="8" to="22" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" from="0.35" to="0" dur="2s" repeatCount="indefinite" />
    </circle>
    {/* Middle ring */}
    <circle r={9} fill={color} opacity={0.2} />
    {/* Solid core */}
    <circle r={6} fill={color} stroke="rgba(255,255,255,0.8)" strokeWidth={1.5} />
  </g>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const RegionalPricingMap = ({ regions }) => {
  if (!regions || regions.length === 0) return null;

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", minHeight: "500px" }}>
      {/* ── SVG Map ── */}
      <ComposableMap
        projection="geoAzimuthalEqualArea"
        projectionConfig={{ scale: 800, center: [15, 52] }}
        style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
      >
        {/* Countries */}
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="rgba(255, 255, 255, 0.04)"
                stroke="rgba(255, 255, 255, 0.18)"
                strokeWidth={0.6}
                style={{
                  default: { outline: "none" },
                  hover: { fill: "rgba(255, 255, 255, 0.09)", outline: "none" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {/* Pulsing dot markers */}
        {regions.map((region) => {
          const cfg = REGION_CONFIG[region.id];
          if (!cfg) return null;
          return (
            <Marker key={region.id} coordinates={cfg.coords}>
              <PulsingDot color={cfg.color} />
            </Marker>
          );
        })}
      </ComposableMap>

      {/* ── HTML Price Cards (absolutely positioned over the SVG) ── */}
      {regions.map((region) => {
        const cfg = REGION_CONFIG[region.id];
        if (!cfg) return null;

        return (
          <Box
            key={`card-${region.id}`}
            sx={{
              position: "absolute",
              top: cfg.cardPos.top,
              left: cfg.cardPos.left,
              // Anchor below the dot so cards never overlap the card header
              transform: "translate(-50%, 0)",
              width: "190px",
              zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            {/* Connector line from dot down to card */}
            <Box
              sx={{
                width: "2px",
                height: "14px",
                background: `linear-gradient(${cfg.color}, transparent)`,
                margin: "0 auto",
              }}
            />

            <RegionCard>
              <Box display="flex" alignItems="center" mb={0.8}>
                <IoLocationOutline color={cfg.color} size={15} style={{ marginRight: "6px", flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.95)", fontWeight: 700, lineHeight: 1.3 }}
                >
                  {region.name}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.45)", display: "block", mb: 1.2, fontSize: "0.68rem" }}
              >
                {region.examples}
              </Typography>

              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.6}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.7rem" }}>
                  WEB 17
                </Typography>
                <Typography variant="caption" sx={{ color: "#60A5FA", fontWeight: 700, fontSize: "0.72rem" }}>
                  {region.our_product.label}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.7rem" }}>
                  Artisse
                </Typography>
                <Typography variant="caption" sx={{ color: "#A78BFA", fontWeight: 700, fontSize: "0.72rem" }}>
                  {region.competitor_product.label}
                </Typography>
              </Box>
            </RegionCard>
          </Box>
        );
      })}
    </Box>
  );
};

export default RegionalPricingMap;
